/**
 * Procedural scene registry (spec section 21).
 *
 * These renderers are the reason BEX LOOP still works with no internet,
 * no API key and no imported footage. They are drawn live by the canvas
 * compositor during export, and drawn once for library thumbnails.
 */

import cityNight from './city-night.js';
import rainWindow from './rain-window.js';
import taxiInterior from './taxi-interior.js';
import metro from './metro.js';
import computerDesk from './computer-desk.js';
import abstractTech from './abstract-tech.js';
import { SILENT_AUDIO } from './shared.js';

export const PROCEDURAL_SCENES = [cityNight, rainWindow, taxiInterior, metro, computerDesk, abstractTech];

const BY_ID = new Map(PROCEDURAL_SCENES.map((scene) => [scene.id, scene]));

/** Used when a scene has no asset and the user explicitly asks for a fallback. */
export const FALLBACK_RENDERER_ID = 'abstract-tech';

export function getProceduralRenderer(id) {
  return BY_ID.get(id) ?? null;
}

export function hasProceduralRenderer(id) {
  return BY_ID.has(id);
}

export function listProceduralScenes() {
  return PROCEDURAL_SCENES.map(({ id, label, description }) => ({ id, label, description }));
}

/**
 * Work out how a scene should be drawn.
 *
 * @returns {{renderer: object|null, isFallback: boolean, reason: string}}
 */
export function resolveRenderer(scene, { allowFallback = false } = {}) {
  if (!scene) return { renderer: null, isFallback: false, reason: 'no scene selected' };
  if (scene.assetType === 'procedural' && scene.proceduralId) {
    const renderer = getProceduralRenderer(scene.proceduralId);
    if (renderer) return { renderer, isFallback: false, reason: 'procedural scene renderer' };
  }
  if (scene.assetType === 'video' && scene.assetUrl) {
    return { renderer: null, isFallback: false, reason: 'imported video asset' };
  }
  if (allowFallback) {
    return {
      renderer: getProceduralRenderer(FALLBACK_RENDERER_ID),
      isFallback: true,
      reason: 'no asset for this scene - using the abstract fallback visual',
    };
  }
  return { renderer: null, isFallback: false, reason: 'this scene has no asset yet' };
}

/**
 * Render a single representative frame, used for library thumbnails.
 * Synchronous and cheap enough to call for every card on screen.
 */
export function drawThumbnail(ctx, scene, { width, height, atPhase = 0.35, audio = SILENT_AUDIO, strength = 0 } = {}) {
  const { renderer } = resolveRenderer(scene, { allowFallback: true });
  if (!renderer) {
    ctx.fillStyle = '#14161C';
    ctx.fillRect(0, 0, width, height);
    return false;
  }
  const loopSeconds = Math.max(4, Number(scene.duration) || 10);
  ctx.save();
  ctx.clearRect(0, 0, width, height);
  renderer.draw(ctx, {
    t: loopSeconds * atPhase,
    loopSeconds,
    w: width,
    h: height,
    audio,
    strength,
    quality: 'thumbnail',
  });
  ctx.restore();
  return true;
}
